// 同意文言一覧
import React, { useState, useEffect } from 'react';
import { render } from 'react-dom';
import axios from '../../../axios';
import { handleBrowserBack } from '../../../BrowserBack';
import {
  organization_type,
  maintenance_type,
  clause_type_list,
  clause_type_list_nourin,
  http_status_code_list,
} from '../../../Const';
import { clause_columns } from '../../../TableColumns';
import Maintenance from '../../../molecules/Maintenance';
import StaffNavbar from '../../../molecules/StaffNavbar';
import { Button } from 'react-bootstrap';
import ReactDatePicker from 'react-datepicker';
import ja from 'date-fns/locale/ja';
import MaintenanceTable from '../../../organisms/MaintenanceTable';
import LoadingModal from '../../../molecules/LoadingModal';
import ValidMessage from '../../../atoms/ValidMessage';

interface ClauseDatas {
  ja_name: string;
  title: string;
  type: string;
  start_date: string;
  end_date: string;
  button: JSX.Element;
}

interface ErrorText {
  jacode?: string;
  target_date?: string;
}

const ClauseList = () => {
  const [datas, setDatas] = useState<ClauseDatas[]>([]);
  // ローディング画面表示状態
  const [is_loading, setIsLoading] = useState(false);
  const [jacode, setJaCode] = useState('');
  const [type, setType] = useState('');
  const [target_date, setTargetDate] = useState<Date | null>(null);
  const [error_text, setErrorText] = useState<ErrorText>();

  // グローバルナビゲーション表示用
  const [role, setRole] = useState('');
  const [has_loan_aproval_system, setHasLoanAprovalSystem] = useState(false);

  // 組織区分コードの取得
  const node = document.getElementById('clause-list');
  const user_organization = node?.dataset.organization_type as string;

  // 同意種類リストの選択肢を格納
  const [clause_select, setClauseSelect] = useState(
    user_organization === organization_type.nourin ? clause_type_list_nourin : clause_type_list
  );

  useEffect(() => {
    // ブラウザの戻る・進む禁止
    handleBrowserBack();
    // テーブルの初期表示を取得（最新世代表示）
    getLatestGeneration();
    // グローバルナビゲーション表示用
    setUserRole();
  }, []);

  // JAコード入力時
  const handleChangeJaCode = (value: string) => {
    setJaCode(value);
  };

  // 種別選択時
  const handleChangeType = (value: string) => {
    setType(value);
  };

  // 基準日選択日
  const handleChangeTargetDate = (value: Date) => {
    setTargetDate(value);
  };

  // レスポンスデータの判定と表示するための加工をする
  const convertDatatableData = (response: any) => {
    setDatas([]);

    // 取得結果の判定
    if (response.result) {
      Object.values(response.list).map((value: any) => {
        setDatas(prev => [
          ...prev,
          {
            ja_name: value.ja_name,
            title: value.title,
            type: value.type,
            start_date: value.start_date,
            end_date: value.end_date,
            button: (
              <Button
                variant="outline-success"
                href={`${location.origin}${process.env.MIX_MOCK_URL}/maintenance/clause_detail?clause_id=${value.id}`}
                className="btn-text-color"
              >
                詳細
              </Button>
            ),
          },
        ]);
      });
    } else {
      if (typeof response.message === 'object') {
        // バリデーションメッセージの表示
        setErrorText({
          jacode: response.message.jacode,
          target_date: response.message.target_date,
        });
      } else {
        console.error(response.message);
        alert(response.message);
      }
    }
  };

  // 検索ボタン押下時
  const searchClause = async () => {
    try {
      setIsLoading(true);
      setErrorText({});
      const response = await axios.post(
        `${location.origin}${process.env.MIX_MOCK_URL}/api/search_clauses`,
        {
          jacode: jacode,
          type: type,
          target_date: target_date,
        }
      );

      // レスポンスデータの判定・加工
      convertDatatableData(response.data);
    } catch (error) {
      console.error(error);
      if (error.response.status === http_status_code_list.unauthorized) {
        // セッション切れの場合はログイン画面に遷移
        location.replace(error.response.data.url);
        return;
      }
      alert(error.response.data.message);
    } finally {
      setIsLoading(false);
    }
  };

  // 最新世代表示ボタン押下時
  const getLatestGeneration = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${location.origin}${process.env.MIX_MOCK_URL}/api/get_latest_generations_clause`
      );

      // レスポンスデータの判定・加工
      convertDatatableData(response.data);
    } catch (error) {
      console.error(error.response.data);
      if (error.response.status === http_status_code_list.unauthorized) {
        // セッション切れの場合はログイン画面に遷移
        location.replace(error.response.data.url);
        return;
      }
      alert(error.response.data.message);
    } finally {
      setIsLoading(false);
    }
  };

  // 全世代表示ボタン押下時
  const getAllGenerations = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${location.origin}${process.env.MIX_MOCK_URL}/api/get_all_generations_clause`
      );

      // レスポンスデータの判定・加工
      convertDatatableData(response.data);
    } catch (error) {
      console.error(error.response.data);
      if (error.response.status === http_status_code_list.unauthorized) {
        // セッション切れの場合はログイン画面に遷移
        location.replace(error.response.data.url);
        return;
      }
      alert(error.response.data.message);
    } finally {
      setIsLoading(false);
    }
  };

  // ロールを設定する
  const setUserRole = () => {
    if (node === null) {
      // nodeが正しく取得できない場合は空文字とする
      setRole('');
      setHasLoanAprovalSystem(false);
      return;
    }

    // ロール名をセット
    setRole(node.dataset.role as string);

    // 融資稟議システム導入済みフラグをセット
    if ((node.dataset.has_loan_aproval_system as string) === '1') {
      setHasLoanAprovalSystem(true);
    } else {
      setHasLoanAprovalSystem(false);
    }

    // 組織区分コードをセット
    const user_organization = node?.dataset.organization_type as string;
  };

  return (
    <>
      <StaffNavbar user_role={role} has_loan_aproval_system={has_loan_aproval_system} />
      <Maintenance active={maintenance_type.clauses}>
        <div>
          <div className="search_field">
            {user_organization === organization_type.shinren ? (
              <div className="search_field-box">
                <label>JAコード</label>
                <div className="input-area">
                  <input
                    title="JAコード"
                    type="text"
                    className={`form-control ${error_text?.jacode && 'textarea-error'}`}
                    value={jacode}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      handleChangeJaCode(event.target.value)
                    }
                  />
                  <ValidMessage message={error_text?.jacode} />
                </div>
              </div>
            ) : (
              <div className="search_field-box"></div>
            )}
            <div className="search_field-box">
              <label>同意種類</label>
              <select
                title="同意種類"
                className="form-control"
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                  handleChangeType(event.target.value)
                }
              >
                <option value=""></option>
                {Object.entries(clause_select).map((value, index) => {
                  return (
                    <option value={value[0]} key={index}>
                      {value[1]}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="search_field-box">
              <label>基準日</label>
              <div className="input-area">
                <ReactDatePicker
                  className={`form-control ${error_text?.target_date && 'textarea-error'}`}
                  selected={target_date}
                  onChange={handleChangeTargetDate}
                  locale={ja}
                  dateFormat="yyyy-MM-dd"
                />
                <ValidMessage message={error_text?.target_date} />
              </div>
            </div>
            <div className="search_field-box">
              <div className="btn-success btn-mini" onClick={searchClause}>
                検索
              </div>
              <div className="btn-success btn-mini" onClick={getLatestGeneration}>
                最新世代表示
              </div>
              <div className="btn-success btn-mini" onClick={getAllGenerations}>
                全世代表示
              </div>
            </div>
          </div>
          <MaintenanceTable datas={datas} columns={clause_columns} params={{}} />
          <div className="footer-btn">
            {/* TODO 遷移先はメンテナンスメニュー作成後に修正する */}
            <a
              className="btn btn-mini btn-outline-success btn-text-color"
              href={`${location.origin}${process.env.MIX_MOCK_URL}/maintenance/menu`}
            >
              戻る
            </a>
          </div>
        </div>
        <LoadingModal show={is_loading} />
      </Maintenance>
    </>
  );
};

if (document.getElementById('clause-list')) {
  render(<ClauseList />, document.getElementById('clause-list'));
}
